import { AssistantType } from 'api/types';
import type { ResourceDefinition } from './types';

export const TEMPLATES_PATH = '/templates';

export enum PROJECT_RESOURCE_CATEGORY {
  BUILD = 'build',
  ANALYZE = 'analyze',
  CHANNEL = 'channel',
  GENERAL = 'general',
  VOICE = 'voice_category',
  CHAT = 'chat',
  DEPLOYMENTS = 'deployments',
  KNOWLEDGE = 'knowledge',
}

const categoryLabels: Record<PROJECT_RESOURCE_CATEGORY, string> = {
  [PROJECT_RESOURCE_CATEGORY.BUILD]: 'Build',
  [PROJECT_RESOURCE_CATEGORY.ANALYZE]: 'Analyze',
  [PROJECT_RESOURCE_CATEGORY.GENERAL]: 'Configure',
  [PROJECT_RESOURCE_CATEGORY.CHANNEL]: 'Channels',
  [PROJECT_RESOURCE_CATEGORY.VOICE]: 'Voice',
  [PROJECT_RESOURCE_CATEGORY.CHAT]: 'Channels',
  [PROJECT_RESOURCE_CATEGORY.DEPLOYMENTS]: 'Deployments',
  [PROJECT_RESOURCE_CATEGORY.KNOWLEDGE]: 'Knowledge',
};

export const routesRegistry = {
  // --- Admin (non-project routes) ---
  USERS: {
    resourceName: null,
    label: 'Users',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/users',
    isPlgResource: false,
    isBranchResource: false,
  },
  DATA_ACCESS: {
    resourceName: null,
    label: 'Data access',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/data-access',
    isPlgResource: false,
    isBranchResource: false,
  },
  API_KEYS: {
    resourceName: null,
    label: 'API Keys',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/api-keys',
    isPlgResource: true,
    isBranchResource: false,
  },
  DATA_ACCESS_API_CONFIGURATION: {
    resourceName: null,
    label: 'Configuration',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/data-access/configuration',
    isPlgResource: false,
    isBranchResource: false,
  },
  USER_SETTINGS: {
    resourceName: null,
    label: 'User settings',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/settings/user',
    isPlgResource: false,
    isBranchResource: false,
  },
  USER_ACCOUNT: {
    resourceName: null,
    label: 'Account',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/settings/account',
    isPlgResource: false,
    isBranchResource: false,
  },
  ADMIN_PAGE: {
    resourceName: null,
    label: 'Admin',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/settings/admin',
    isPlgResource: false,
    isBranchResource: false,
  },

  // --- Assistant ---
  ASSISTANTS: {
    resourceName: null,
    label: 'Agents',
    projectType: AssistantType.GENAI,
    path: '/:accountId',
    isPlgResource: false,
    isBranchResource: false,
    isSampleProjectResource: false,
  },
  // --- MANAGE ---
  BILLING: {
    resourceName: 'billing',
    label: 'Billing',
    readableResourceName: 'Billing',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/billing',
    isPlgResource: false,
    isBranchResource: false,
  },
  SECRETS: {
    resourceName: 'secrets',
    label: 'Secrets',
    readableResourceName: 'Secrets',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/secrets',
    isPlgResource: false,
    isBranchResource: false,
  },
  USER_MANAGEMENT: {
    resourceName: 'user_management',
    label: 'User management',
    readableResourceName: 'User management',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: null,
    isPlgResource: false,
    isBranchResource: false,
  },
  HOME: {
    resourceName: 'home',
    label: 'Home',
    readableResourceName: 'Home',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/home',
    isPlgResource: true,
    isBranchResource: false,
  },
  ANALYTICS_HOME: {
    resourceName: null,
    label: 'Home',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/home',
    isPlgResource: true,
    isBranchResource: false,
  },
  CONVERSATIONS: {
    resourceName: 'conversations',
    label: 'Conversations',
    readableResourceName: 'Conversations',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/conversations',
    isPlgResource: false,
    isBranchResource: false,
  },
  INTENTS: {
    resourceName: 'intents',
    label: 'Intents',
    readableResourceName: 'Intents',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: null,
    isPlgResource: false,
    isBranchResource: false,
  },
  ENTITIES: {
    resourceName: 'entities',
    label: 'Entities',
    readableResourceName: 'Entities',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: null,
    isPlgResource: false,
    isBranchResource: false,
  },
  METRICS: {
    resourceName: 'metrics',
    label: 'Metrics',
    readableResourceName: 'Metrics',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: null,
    isPlgResource: false,
    isBranchResource: false,
  },
  PII: {
    resourceName: 'pii',
    label: 'Pii',
    readableResourceName: 'PII',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: null,
    isPlgResource: false,
    isBranchResource: false,
  },
  CALL_DOWNLOAD: {
    resourceName: 'call_download',
    label: 'Call download',
    readableResourceName: 'Call download',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: null,
    isPlgResource: false,
    isBranchResource: false,
  },
  ASSISTANT_ANALYSIS: {
    resourceName: 'assistant_analysis',
    label: 'Agent analysis',
    readableResourceName: 'Agent analysis',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/assistant-analysis',
    isPlgResource: false,
    isBranchResource: false,
  },
  CREATE_ANALYSIS: {
    resourceName: 'create_analysis',
    label: 'Create analysis',
    readableResourceName: 'Create analysis',
    pageTitle: 'New analysis',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/create-analysis',
    isPlgResource: false,
    isBranchResource: false,
  },
  REALTIME_CONFIG_MANAGER: {
    resourceName: 'realtime_config_manager',
    label: 'Real time configuration',
    readableResourceName: 'Real time configuration',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/real-time-configuration/:clientEnv',
    isPlgResource: false,
    isBranchResource: false,
  },
  REAL_TIME_CONFIG_FORM: {
    resourceName: null,
    label: 'Real time configuration',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/real-time-configuration/:clientEnv',
    isPlgResource: false,
    isBranchResource: false,
  },
  SMART_ANALYST: {
    resourceName: 'smart_analyst',
    label: 'Smart analyst',
    readableResourceName: 'Smart analyst',
    pageTitle: 'Smart Analyst',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/smart-analyst',
    isPlgResource: false,
    isBranchResource: false,
  },
  CUSTOM_METRICS: {
    resourceName: 'custom_metrics',
    label: 'Custom Metrics',
    readableResourceName: 'Metrics',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/custom-metrics',
    isPlgResource: false,
    isBranchResource: false,
  },
  ANALYSIS_DASHBOARD: {
    resourceName: 'analysis_dashboard',
    label: 'Analysis dashboard',
    readableResourceName: 'Analysis dashboard',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/:batchId/analysis-dashboard',
    isPlgResource: false,
    isBranchResource: false,
  },

  // --- ANALYZE (sub-routes) ---
  ANALYTICS: {
    resourceName: 'analytics',
    label: 'Overview',
    readableResourceName: 'Overview',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/overview',
    isPlgResource: false,
    isBranchResource: false,
  },
  ANALYTICS_OVERVIEW: {
    resourceName: null,
    label: 'Overview',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/overview',
    isPlgResource: false,
    isBranchResource: false,
  },
  SAFETY_ANALYTICS: {
    resourceName: 'safety_analytics',
    label: 'Enterprise safety',
    readableResourceName: 'Enterprise safety',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/enterprise-safety',
    isPlgResource: false,
    isBranchResource: false,
  },
  ANALYTICS_SAFETY: {
    resourceName: null,
    label: 'Enterprise safety',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/enterprise-safety',
    isPlgResource: false,
    isBranchResource: false,
  },
  CUSTOM_ANALYTICS: {
    resourceName: 'custom_analytics',
    label: 'Custom dashboards',
    readableResourceName: 'Dashboards',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/analytics/:dashboardSlug',
    isPlgResource: false,
    isBranchResource: false,
  },
  JUPITER_ANALYTICS_DASHBOARD: {
    resourceName: null,
    label: 'Custom Analytics Dashboard',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/analytics/:dashboardSlug',
    isPlgResource: false,
    isBranchResource: false,
  },
  CONVERSATION_REVIEW: {
    resourceName: null,
    label: 'Conversation',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/conversations/:conversationId',
    isPlgResource: false,
    isBranchResource: false,
  },
  LATEST_CONVERSATION: {
    resourceName: null,
    label: 'Latest Conversation',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/conversations/latest',
    isPlgResource: false,
    isBranchResource: false,
  },
  QUICKSIGHT_ANALYTICS_DASHBOARD: {
    resourceName: null,
    label: 'Custom Analytics Dashboard',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/dashboards/:dashboardSlug',
    isPlgResource: false,
    isBranchResource: false,
  },
  CREATE_PERFORMANCE_OVER_TIME_ANALYSIS: {
    resourceName: null,
    label: 'New performance over time analysis',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/performance-over-time-analysis/create',
    isPlgResource: false,
    isBranchResource: false,
  },
  GET_PERFORMANCE_OVER_TIME_ANALYSIS: {
    resourceName: null,
    label: 'Performance over time',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/performance-over-time-analyses/:scheduleId',
    isPlgResource: false,
    isBranchResource: false,
  },
  ASSISTANT_ANALYSIS_DETAILS: {
    resourceName: null,
    label: 'Analysis configuration',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/assistant-analysis/task/:taskId',
    isPlgResource: false,
    isBranchResource: false,
  },
  CREATE_ANALYSIS_TASK: {
    resourceName: null,
    label: 'New Analysis configuration',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/assistant-analysis/task/create',
    isPlgResource: false,
    isBranchResource: false,
  },
  SMART_ANALYST_CHAT: {
    resourceName: null,
    label: 'Smart Analyst Chat',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/smart-analyst/chats/:chatId',
    isPlgResource: false,
    isBranchResource: false,
  },
  LATENCY_VIZ: {
    resourceName: null,
    label: 'Latency visualization',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/conversations/:conversationId/latency-viz',
    isPlgResource: false,
    isBranchResource: false,
  },

  // --- BUILD ---
  STUDIO_ASSISTANT_ONBOARDING: {
    resourceName: null,
    label: 'Create Agent',
    pageTitle: 'Studio Assistant',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/studio-assistant/onboarding',
    isPlgResource: false,
    isBranchResource: false,
  },
  STUDIO_ASSISTANT: {
    resourceName: null,
    label: 'Studio Assistant',
    pageTitle: 'Studio Assistant',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/studio-assistant/:sessionId?',
    isPlgResource: false,
    isBranchResource: false,
  },
  PLG_HOME: {
    resourceName: null,
    label: 'Home',
    pageTitle: 'Studio Assistant',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/home/studio-assistant/:sessionId?',
    isPlgResource: true,
    isBranchResource: false,
  },
  PLG_ANALYTICS_DASHBOARD: {
    resourceName: null,
    label: 'Dashboard',
    pageTitle: 'Dashboard',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/dashboard',
    isPlgResource: true,
    isBranchResource: false,
  },
  KNOWLEDGE_BASE: {
    resourceName: 'knowledge_base',
    label: 'Knowledge base',
    readableResourceName: 'Managed topics',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/knowledge-base',
    isPlgResource: true,
    isBranchResource: true,
  },
  AGENT: {
    resourceName: 'about',
    label: 'Agent',
    readableResourceName: 'Agent',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/agent',
    isPlgResource: true,
    isBranchResource: true,
  },
  RULES: {
    resourceName: 'rules',
    label: 'Behavior',
    readableResourceName: 'Behavior',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/agent',
    isPlgResource: true,
    isBranchResource: true,
  },
  SPEECH_RECOGNITION: {
    resourceName: 'speech_recognition',
    label: 'Speech recognition',
    readableResourceName: 'Speech recognition',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/speech-recognition/:type',
    isPlgResource: true,
    isBranchResource: true,
  },
  TRANSCRIPT_CORRECTIONS_CREATE_OR_UPDATE: {
    resourceName: 'speech_recognition',
    label: '',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/speech-recognition/transcript-corrections/:transcriptCorrectionId',
    isPlgResource: true,
    isBranchResource: true,
  },
  STOP_KEYWORD_CREATE_OR_UPDATE: {
    resourceName: 'speech_recognition',
    label: '',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/response-control/phrase-filtering/:stopKeywordId',
    isPlgResource: true,
    isBranchResource: true,
  },
  RESPONSE_CONTROL: {
    resourceName: 'response_control',
    label: 'Response control',
    readableResourceName: 'Response control',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/response-control/:type',
    isPlgResource: true,
    isBranchResource: true,
  },
  OVERWRITE_RULE_CREATE_OR_UPDATE: {
    path: '/:accountId/:projectId/response-control/overwrite-rules/:overwriteRuleId',
    resourceName: 'response_control',
    label: '',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    isPlgResource: true,
    isBranchResource: true,
  },
  FUNCTIONS: {
    resourceName: 'functions',
    label: 'Tools',
    readableResourceName: 'Tools',
    pageTitle: 'Functions',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/functions',
    isPlgResource: true,
    isBranchResource: true,
  },
  ASSISTANT_FUNCTIONS: {
    resourceName: null,
    label: 'Functions',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/functions',
    isPlgResource: false,
    isBranchResource: false,
  },
  ASSISTANT_FUNCTION: {
    resourceName: null,
    label: 'Function',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/functions/:functionId',
    isPlgResource: false,
    isBranchResource: false,
  },
  APIS: {
    resourceName: 'apis',
    label: 'APIs',
    readableResourceName: 'APIs',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/apis',
    isPlgResource: false,
    isBranchResource: false,
  },
  APIS_CREATE: {
    resourceName: 'apis',
    label: 'Create API',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/apis/create',
    isPlgResource: false,
    isBranchResource: false,
  },
  APIS_EDIT: {
    resourceName: 'apis',
    label: 'Edit API',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/apis/:apiId',
    isPlgResource: false,
    isBranchResource: false,
  },
  KNOWLEDGE: {
    resourceName: 'knowledge',
    label: 'Knowledge',
    readableResourceName: 'Knowledge',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/knowledge/:type',
    isPlgResource: false,
    isBranchResource: false,
  },
  AS_API_INTEGRATIONS: {
    resourceName: 'as_api_integrations',
    label: 'APIs',
    readableResourceName: 'APIs',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: null,
    isPlgResource: false,
    isBranchResource: false,
  },
  HANDOFFS: {
    resourceName: 'handoffs',
    label: 'Call handoffs',
    readableResourceName: 'Call handoffs',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/handoff',
    isPlgResource: false,
    isBranchResource: true,
  },
  HANDOFF: {
    resourceName: null,
    label: 'Call handoffs',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/handoff',
    isPlgResource: false,
    isBranchResource: false,
  },
  SMS: {
    resourceName: 'sms',
    label: 'Sms',
    readableResourceName: 'Sms',
    pageTitle: 'SMS messaging',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/sms-templates',
    isPlgResource: false,
    isBranchResource: true,
  },
  SMS_TEMPLATES: {
    resourceName: null,
    label: 'SMS messaging',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/sms-templates',
    isPlgResource: false,
    isBranchResource: false,
  },
  FLOWS: {
    resourceName: 'jupiter_flows',
    label: 'Flows',
    readableResourceName: 'Flows',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/flows',
    isPlgResource: true,
    isBranchResource: true,
  },
  FLOW_DETAILS: {
    resourceName: null,
    label: 'Flow details',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/flows/:flowId',
    isPlgResource: false,
    isBranchResource: false,
  },
  REALTIME_CONFIG_BUILDER: {
    resourceName: 'realtime_config_builder',
    label: 'Configuration builder',
    readableResourceName: 'Configuration builder',
    pageTitle: 'Real Time Config',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/real-time-config',
    isPlgResource: false,
    isBranchResource: false,
  },
  REAL_TIME_CONFIG_FILES: {
    resourceName: null,
    label: 'Real Time Config',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/real-time-config',
    isPlgResource: false,
    isBranchResource: false,
  },
  REAL_TIME_CONFIG_SCHEMA: {
    resourceName: null,
    label: 'Real Time Config - Schema',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/real-time-config/:clientEnv/schema',
    isPlgResource: false,
    isBranchResource: false,
  },
  REAL_TIME_CONFIG_DATA: {
    resourceName: null,
    label: 'Real Time Config - Data',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/real-time-config/:clientEnv/data',
    isPlgResource: false,
    isBranchResource: false,
  },
  TEST_SUITE: {
    resourceName: 'testing',
    label: 'Test suite',
    readableResourceName: 'Test suite',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/test-suite/:type',
    isPlgResource: false,
    isBranchResource: false,
  },
  TEST_SET: {
    resourceName: null,
    label: 'Test set',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/test-suite/test-sets/:testSetId/:type',
    isPlgResource: false,
    isBranchResource: false,
  },
  TEST_CASE: {
    resourceName: null,
    label: 'Test case',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/test-suite/test-cases/:testCaseId',
    isPlgResource: false,
    isBranchResource: false,
  },
  LANGUAGE_HUB: {
    resourceName: 'language_hub',
    label: 'Language hub',
    readableResourceName: 'Language hub',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/language-hub',
    isPlgResource: false,
    isBranchResource: false,
  },
  SOURCE_HUB: {
    resourceName: 'source_hub',
    label: 'Connected knowledge',
    readableResourceName: 'Connected',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/knowledge/connected',
    isPlgResource: false,
    isBranchResource: false,
  },
  VARIANT_MANAGEMENT: {
    resourceName: 'variant_management',
    label: 'Variant management',
    readableResourceName: 'Variant management',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: true,
    path: '/:accountId/:projectId/variant-management',
    isPlgResource: true,
    isBranchResource: true,
  },
  BRANCH_MANAGEMENT: {
    resourceName: 'branch_management',
    label: 'Branch management',
    readableResourceName: 'Branch management',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: null,
    isPlgResource: false,
    isBranchResource: false,
  },
  SITES: {
    resourceName: null,
    label: 'Sites',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/sites',
    isPlgResource: false,
    isBranchResource: false,
  },
  SITE: {
    resourceName: null,
    label: 'Site',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/sites/:siteId',
    isPlgResource: false,
    isBranchResource: false,
  },
  VOICE_LIBRARY: {
    resourceName: 'voice',
    label: 'Voice library',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/voice/library',
    isPlgResource: true,
    isBranchResource: true,
  },
  WIDGET_CONFIGURATION: {
    resourceName: 'widget_configuration',
    label: 'Widget configurations',
    readableResourceName: 'Widget',
    projectType: AssistantType.GENAI,
    path: '/:accountId/:projectId/channel/webchat/widget-configuration',
    isPlgResource: false,
    isBranchResource: false,
    isSampleProjectResource: true,
  },

  // --- RESPONSE ---
  VOICE: {
    resourceName: 'voice',
    label: 'Agent voice',
    readableResourceName: 'Agent voice',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/voice',
    isPlgResource: true,
    isBranchResource: true,
  },
  AUDIO_MANAGEMENT: {
    resourceName: 'audio_cache',
    label: 'Cache management',
    readableResourceName: 'Audio management',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/audio-management',
    isPlgResource: false,
    isBranchResource: false,
  },

  // --- CHANNEL ---
  VOICE_CHANNEL: {
    resourceName: 'voice_channel',
    label: 'Voice',
    readableResourceName: 'Voice configuration',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/channel/voice',
    isPlgResource: false,
    isBranchResource: false,
  },
  WEBCHAT_CHANNEL: {
    resourceName: 'webchat_channel',
    label: 'Web chat',
    readableResourceName: 'Chat configuration',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/channel/webchat',
    isPlgResource: false,
    isBranchResource: false,
  },
  MODEL_CONFIGURATION: {
    resourceName: 'voice_channel',
    label: 'Model configuration',
    readableResourceName: 'Model configuration',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/channel/voice/models',
    isPlgResource: false,
    isBranchResource: false,
  },

  // --- GENERAL (Configure) ---
  ENVIRONMENTS: {
    resourceName: 'environments',
    label: 'Environments',
    readableResourceName: 'Environments',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/environments',
    isPlgResource: true,
    isBranchResource: false,
  },
  ENVIRONMENTS_COMPARISON: {
    resourceName: null,
    label: 'Knowledge base comparison',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/environments/comparison',
    isPlgResource: false,
    isBranchResource: false,
  },
  SETTINGS: {
    resourceName: 'settings',
    label: 'Settings',
    readableResourceName: 'General',
    projectType: AssistantType.HYBRID,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/settings',
    isPlgResource: true,
    isBranchResource: true,
  },
  SETTINGS_TEMPLATE_UI: {
    resourceName: null,
    label: 'Template Settings',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/settings/template-ui',
    isPlgResource: false,
    isBranchResource: false,
  },
  INTEGRATIONS: {
    resourceName: 'integrations',
    label: 'Integrations',
    readableResourceName: 'Integrations',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/integrations',
    isPlgResource: false,
    isBranchResource: false,
  },
  AGENT_CONFIG: {
    resourceName: 'agent_config',
    label: 'Agent config',
    readableResourceName: 'Agent config',
    projectType: AssistantType.INTENT,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/config/:fileId',
    isPlgResource: false,
    isBranchResource: false,
  },
  NUMBERS: {
    resourceName: 'numbers',
    label: 'Numbers',
    readableResourceName: 'Numbers',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/numbers',
    isPlgResource: false,
    isBranchResource: false,
  },
  WEBCHAT_CONFIGURATIONS: {
    resourceName: 'webchat_configurations',
    label: 'Webchat configurations',
    readableResourceName: 'Widget',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: null,
    isPlgResource: false,
    isBranchResource: false,
  },
  CSAT: {
    resourceName: 'csat',
    label: 'CSAT',
    readableResourceName: 'CSAT',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/csat',
    isPlgResource: false,
    isBranchResource: false,
  },
  PROJECT_HISTORY: {
    resourceName: null,
    label: 'Project history',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/history',
    isPlgResource: false,
    isBranchResource: false,
  },
  DASHBOARDS: {
    resourceName: null,
    label: 'Dashboards',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/:projectId/dashboards-settings',
    isPlgResource: false,
    isBranchResource: false,
  },

  // --- Integrations ---
  OPENTABLE_REGION: {
    resourceName: null,
    label: 'OpenTable select region',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/ot-select-region',
    isPlgResource: false,
    isBranchResource: false,
    isPublicPath: true,
  },
  OPENTABLE_LOGIN: {
    resourceName: null,
    label: 'OpenTable login',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/ot-login',
    isPlgResource: false,
    isBranchResource: false,
    isPublicPath: true,
  },
  OPENTABLE_CALLBACK: {
    resourceName: null,
    label: 'OpenTable callback',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/ot-callback',
    isPlgResource: false,
    isBranchResource: false,
    isPublicPath: true,
  },
  OPENTABLE_WORKSPACE: {
    resourceName: null,
    label: 'OpenTable link workspace',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/integrations/opentable/link-workspace',
    isPlgResource: false,
    isBranchResource: false,
  },
  OPENTABLE_NEW_AGENT: {
    resourceName: null,
    label: 'OpenTable new agent',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/integrations/opentable/new-agent',
    isPlgResource: false,
    isBranchResource: false,
  },
  NICE_BYOC_REDIRECT: {
    resourceName: null,
    label: 'NICE BYOC redirect',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/integrations/nice-byoc-redirect',
    isPlgResource: false,
    isBranchResource: false,
    isPublicPath: true,
  },
  TRIAL_EXPIRED: {
    resourceName: null,
    label: 'Trial Expired',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/:accountId/trial-expired',
    isPlgResource: false,
    isBranchResource: false,
  },

  // --- Auth ---
  LOGOUT: {
    resourceName: null,
    label: 'Logout',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/logout',
    isPlgResource: false,
    isBranchResource: false,
    isPublicPath: true,
  },
  PARAGON_OAUTH_REDIRECT: {
    resourceName: null,
    label: 'OAuth Redirect',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/oauth-redirect',
    isPlgResource: false,
    isBranchResource: false,
    isPublicPath: true,
  },
  CALLBACK: {
    resourceName: null,
    label: 'Callback',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/callback',
    isPlgResource: false,
    isBranchResource: false,
  },

  // Shareable WebRTC
  WEBRTC_DEMO: {
    resourceName: null,
    label: 'WebRTC Demo',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: '/demo/:demoId',
    isPlgResource: false,
    isBranchResource: false,
    isPublicPath: true,
    skipAuthRedirect: true,
  },

  // --- Templates (public sample project preview) ---
  TEMPLATES: {
    resourceName: null,
    label: 'Templates',
    projectType: AssistantType.GENAI,
    isSampleProjectResource: false,
    path: TEMPLATES_PATH,
    isPlgResource: false,
    isBranchResource: false,
    isPublicPath: true,
  },
} as const satisfies Record<string, ResourceDefinition>;

// --- Derived types ---

type RegistryKey = keyof typeof routesRegistry;
type Registry = typeof routesRegistry;

type ResourceKey = {
  [K in RegistryKey]: Registry[K]['resourceName'] extends null ? never : K;
}[RegistryKey];

type PathKey = {
  [K in RegistryKey]: Registry[K]['path'] extends null ? never : K;
}[RegistryKey];

// --- Derived: resource values & type ---

const resourceEntries = Object.values(routesRegistry).filter(
  (r): r is typeof r & { resourceName: string } => r.resourceName !== null,
);

export const PROJECT_RESOURCE = Object.fromEntries(
  Object.entries(routesRegistry)
    .filter(([_, v]) => v.resourceName !== null)
    .map(([k, v]) => [k, v.resourceName]),
) as { [K in ResourceKey]: Registry[K]['resourceName'] };

export type ProjectResource =
  (typeof PROJECT_RESOURCE)[keyof typeof PROJECT_RESOURCE];

// --- Derived: lookup maps ---

export const featureToCopyMap: Record<string, string> = {
  ...categoryLabels,
  ...Object.fromEntries(
    resourceEntries
      .filter(r => 'readableResourceName' in r && !!r.readableResourceName)
      .map(r => [
        r.resourceName,
        (r as typeof r & { readableResourceName: string }).readableResourceName,
      ]),
  ),
};

export const SAMPLE_PROJECTS_RESOURCES = Object.values(routesRegistry)
  .filter(r => r.isSampleProjectResource)
  .map(r => r.resourceName);

export const RESOURCE_PROJECT_TYPE_MAP: Record<string, AssistantType> =
  Object.fromEntries(resourceEntries.map(r => [r.resourceName, r.projectType]));

export const PROJECT_RESOURCE_PATH_MAP = Object.fromEntries(
  resourceEntries
    .filter(r => r.path !== null && r.path.includes(':projectId'))
    .map(r => [r.resourceName, r.path]),
) as { [K in ProjectResource]?: string };

export const PLG_RESOURCES = resourceEntries
  .filter(r => r.isPlgResource)
  .map(r => r.resourceName) as ProjectResource[];

export const BRANCH_RESOURCE_SET = new Set<string>(
  resourceEntries.filter(r => r.isBranchResource).map(r => r.resourceName),
);

// --- Derived: PATHS & PAGE_TITLES ---

export type PathsMap = {
  [K in PathKey]: NonNullable<Registry[K]['path']>;
};

export const PATHS = Object.fromEntries(
  Object.entries(routesRegistry)
    .filter(([_, r]) => r.path !== null)
    .map(([k, r]) => [k, r.path]),
) as PathsMap;

export const PAGE_TITLES = Object.fromEntries(
  Object.entries(routesRegistry)
    .filter(([_, r]) => r.path !== null)
    .map(([k, r]) => [k, (r as ResourceDefinition).pageTitle ?? r.label]),
) as { [K in PathKey]: string };

// Paths that are accessible without authentication.
// Full parameterised paths are kept (e.g. /demo/:demoId) — consumers must use
// segment-aware matching (matchPath) rather than startsWith.
export const PUBLIC_PATHS: string[] = [
  ...(Object.values(routesRegistry) as ResourceDefinition[])
    .filter(r => r.isPublicPath && r.path !== null)
    .map(r => r.path!),
];

// Paths where 401s and token failures should not trigger login/logout redirects.
// Some public routes (e.g. WebRTC demo) are used by unauthenticated visitors
// such as prospects and potential customers. Authenticated API calls (e.g.
// feature flags) can still fire on these pages via shared hooks, and on
// environments that require auth they will 401. Without this guard, those 401s
// cascade into getAccessTokenSilently → handleLogout, redirecting the visitor
// to the login page. Routes marked skipAuthRedirect suppress that redirect so
// unauthenticated users can use the page without being bounced to Auth0.
export const SKIP_AUTH_REDIRECT_PATHS: string[] = [
  ...(Object.values(routesRegistry) as ResourceDefinition[])
    .filter(r => r.skipAuthRedirect && r.path !== null)
    .map(r => r.path!),
];
